import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./Auth/AuthContext";
import { fetchTransactions } from "../utils/shopifyApi";

const PAGE_SIZE = 20;

const styles = {
    container: {
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "24px",
        boxSizing: "border-box",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        marginBottom: '24px'
    },
    breadcrumbLink: {
        color: '#666',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s'
    },
    breadcrumbSeparator: {
        color: '#999'
    },
    title: {
        fontSize: "24px",
        fontWeight: 700,
        color: "#1a1a1a",
        margin: "0 0 20px 0",
    },
    tableWrap: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.06)",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
    },
    th: {
        textAlign: "left",
        padding: "12px 16px",
        fontWeight: 600,
        color: "#374151",
        backgroundColor: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
    },
    td: {
        padding: "12px 16px",
        borderBottom: "1px solid #f3f4f6",
        color: "#374151",
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 500,
    },
    pagination: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "16px",
        fontSize: "14px",
        color: "#6b7280",
    },
    pageButtons: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
    },
    pageButton: {
        padding: "8px 14px",
        backgroundColor: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        color: "#374151",
    },
    pageButtonDisabled: {
        padding: "8px 14px",
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        fontSize: "14px",
        color: "#9ca3af",
        cursor: "default",
    },
    stateContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "280px",
        fontSize: "15px",
        color: "#6b7280",
    },
    errorContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "280px",
        gap: "12px",
    },
    errorText: { fontSize: "15px", color: "#ef4444" },
    retryButton: {
        padding: "8px 20px",
        backgroundColor: "#111827",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
    },
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { dateStyle: "short", timeStyle: "short" });
    } catch {
        return iso;
    }
}

function statusBadge(status) {
    if (!status) return <span style={{ color: "#9ca3af" }}>—</span>;
    const colors = {
        success: "#d1fae5",
        pending: "#fef3c7",
        failure: "#fee2e2",
        error: "#fee2e2",
    };
    const color = colors[String(status).toLowerCase()] || "#f3f4f6";
    return (
        <span style={{ ...styles.badge, backgroundColor: color, color: "#374151" }}>
            {status}
        </span>
    );
}

export default function TransactionsList() {
    const { tokens } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const load = useCallback(
        async (pageNum) => {
            setLoading(true);
            setError(null);
            const result = await fetchTransactions({
                page: pageNum,
                limit: PAGE_SIZE,
                accessToken: tokens?.accessToken,
            });
            if (result.ok && result.data) {
                setTransactions(result.data.transactions ?? []);
                setTotal(result.data.total ?? 0);
            } else {
                setError(result.error || "Failed to load transactions");
            }
            setLoading(false);
        },
        [tokens?.accessToken]
    );

    useEffect(() => {
        load(page);
    }, [page, load]);

    if (loading && transactions.length === 0) {
        return (
            <div style={styles.container}>
                {/* Breadcrumb */}
                <div style={styles.breadcrumb}>
                    <span style={{color: '#1a1a1a', fontWeight: 500}}>Transactions</span>
                </div>
                
                <h1 style={styles.title}>Transactions</h1>
                <div style={styles.stateContainer}>Loading transactions…</div>
            </div>
        );
    }

    if (error && transactions.length === 0) {
        return (
            <div style={styles.container}>
                {/* Breadcrumb */}
                <div style={styles.breadcrumb}>
                    <span style={{color: '#1a1a1a', fontWeight: 500}}>Transactions</span>
                </div>
                
                <h1 style={styles.title}>Transactions</h1>
                <div style={styles.errorContainer}>
                    <span style={styles.errorText}>{error}</span>
                    <button style={styles.retryButton} onClick={() => load(page)}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <span style={{color: '#1a1a1a', fontWeight: 500}}>Transactions</span>
            </div>
            
            <h1 style={styles.title}>Transactions</h1>
            <div style={styles.tableWrap}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Ref ID</th>
                            <th style={styles.th}>Order Ref</th>
                            <th style={styles.th}>Amount</th>
                            <th style={styles.th}>Kind</th>
                            <th style={styles.th}>Gateway</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Source</th>
                            <th style={styles.th}>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ ...styles.td, textAlign: "center", color: "#9ca3af" }}>
                                    No transactions found
                                </td>
                            </tr>
                        ) : (
                            transactions.map((t) => (
                                <tr key={t.id}>
                                    <td style={styles.td}>{t.refId ?? "—"}</td>
                                    <td style={styles.td}>{t.orderRefId ?? "—"}</td>
                                    <td style={styles.td}>
                                        {t.amount != null ? `${t.currency || "USD"} ${t.amount}` : "—"}
                                    </td>
                                    <td style={styles.td}>{t.kind ?? "—"}</td>
                                    <td style={styles.td}>{t.gateway ?? "—"}</td>
                                    <td style={styles.td}>{statusBadge(t.status)}</td>
                                    <td style={styles.td}>{t.sourceRef ?? "—"}</td>
                                    <td style={styles.td}>{formatDate(t.createdAt)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {total > 0 && (
                <div style={styles.pagination}>
                    <span>
                        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                    </span>
                    <div style={styles.pageButtons}>
                        <button
                            style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                        >
                            Previous
                        </button>
                        <span>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
